/**
 * schema.org/Event as JSON-LD — the format that makes an event machine-readable
 * on a page (rich results, calendar-aware browsers, scrapers), rather than a file
 * a user downloads.
 */

import { type EventDraft, timeClock, timeDate, draftTimeZone, effectiveEnd } from './model';

export interface JsonLdEvent {
  readonly '@context': 'https://schema.org';
  readonly '@type': 'Event';
  readonly name: string;
  readonly startDate: string;
  readonly endDate?: string;
  readonly description?: string;
  readonly url?: string;
  readonly eventStatus?: string;
  readonly location?: { readonly '@type': 'Place'; readonly name: string };
  readonly organizer?: {
    readonly '@type': 'Person';
    readonly name?: string;
    readonly email: string;
  };
  readonly attendee?: readonly {
    readonly '@type': 'Person';
    readonly name?: string;
    readonly email: string;
  }[];
}

const STATUS_URLS: Record<string, string> = {
  confirmed: 'https://schema.org/EventScheduled',
  tentative: 'https://schema.org/EventScheduled',
  cancelled: 'https://schema.org/EventCancelled',
};

/** ISO 8601 for schema.org: a date for all-day, wall-clock otherwise (zone names are not ISO offsets, so they are dropped). */
function isoValue(draft: EventDraft, which: 'start' | 'end'): string {
  const time = which === 'start' ? draft.start : effectiveEnd(draft);
  if (time.kind === 'date') return time.date;
  const local = `${timeDate(time)}T${timeClock(time) ?? '00:00:00'}`;
  return draftTimeZone(draft) === 'UTC' ? `${local}Z` : local;
}

export function toJsonLd(draft: EventDraft): JsonLdEvent {
  const person = (p: { name?: string; email: string }) =>
    p.name === undefined
      ? ({ '@type': 'Person', email: p.email } as const)
      : ({ '@type': 'Person', name: p.name, email: p.email } as const);

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: draft.title,
    startDate: isoValue(draft, 'start'),
    ...(draft.end === undefined ? {} : { endDate: isoValue(draft, 'end') }),
    ...(draft.description === undefined || draft.description === ''
      ? {}
      : { description: draft.description }),
    ...(draft.url === undefined || draft.url === '' ? {} : { url: draft.url }),
    ...(draft.status === undefined ? {} : { eventStatus: STATUS_URLS[draft.status] }),
    ...(draft.location === undefined || draft.location === ''
      ? {}
      : { location: { '@type': 'Place' as const, name: draft.location } }),
    ...(draft.organizer === undefined ? {} : { organizer: person(draft.organizer) }),
    ...((draft.attendees ?? []).length === 0
      ? {}
      : { attendee: (draft.attendees ?? []).map(person) }),
  };
}

/** The `<script type="application/ld+json">` payload, pretty-printed. */
export function toJsonLdScript(draft: EventDraft): string {
  return JSON.stringify(toJsonLd(draft), null, 2);
}
